import { useState, useEffect, useRef } from 'react';
import {
	Button,
	Card,
	Divider,
	Empty,
	Flex,
	Input,
	List,
	Modal,
	Radio,
	Select,
	Spin,
} from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';

import KeyenceCard from '../../../../components/Operations/Quality/KeyenceCard';
import {
	useGetInspectionPlansQuery,
	useGetRecentFileQuery,
	useGetKeyenceSystemsQuery,
} from '../../../../redux/api/quality';
import { InspectionPlan } from '../../../../interfaces/Quality.Interface';
import { usePolling } from '../../../../hooks/usePolling';
import BuildingIndicator from '../../../../components/BuildingIndicator';
import { useBuildingContext } from '../../../../context/BuildingContext';
import {
	TABLET_TITLE_STYLE,
	TABLET_BACK_BUTTON_STYLE,
	TABLET_SEARCH_STYLE,
	TABLET_RADIO_BUTTON_STYLE,
	TABLET_PRODUCTION_METHOD_ROW_STYLE,
	TABLET_PRODUCTION_METHOD_GROUP_STYLE,
} from '../../../../components/Operations/tabletStyles';

const LEGACY_KEYENCE_SYSTEM_LS_KEY = 'keyence_system';
const keyenceSystemLsKey = (buildingId?: string | null) =>
	buildingId ? `keyence_system_${buildingId}` : LEGACY_KEYENCE_SYSTEM_LS_KEY;

const KeyenceLineItemList: React.FC = () => {
	const navigate = useNavigate();
	const { buildingId } = useBuildingContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const initialSearchTerm = searchParams.get('search') || '';
	const initialStatus = searchParams.get('status') || 'Live';

	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
	const [status, setStatus] = useState(initialStatus);
	const [pollingEnabled, setPollingEnabled] = useState(true);
	const [showSampleViewModal, setShowSampleViewModal] = useState(false);
	const [skipSampleRender, setSkipSampleRender] = useState(false);
	const [lastSeenFileName, setLastSeenFileName] = useState<string | null>(null);
	const [selectedSystem, setSelectedSystem] = useState<string>(() => {
		const key = keyenceSystemLsKey(buildingId);
		const perBuilding = localStorage.getItem(key);
		if (perBuilding) return perBuilding;
		return localStorage.getItem(LEGACY_KEYENCE_SYSTEM_LS_KEY) || '';
	});
	const [page, setPage] = useState(1);
	const [allItems, setAllItems] = useState<InspectionPlan[]>([]);
	const [productionMethod, setProductionMethod] = useState<string>(
		searchParams.get('method') || ''
	);
	const contentRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);

	const { data: systems, isSuccess: systemsLoaded } = useGetKeyenceSystemsQuery(
		buildingId ? { building_id: buildingId } : undefined,
		{ refetchOnMountOrArgChange: true }
	);

	// When the building context changes, restore the remembered system for that
	// building (or fall back to legacy global persistence on first switch).
	useEffect(() => {
		const key = keyenceSystemLsKey(buildingId);
		const perBuilding = localStorage.getItem(key);
		if (perBuilding) {
			setSelectedSystem(perBuilding);
		} else {
			const legacy = localStorage.getItem(LEGACY_KEYENCE_SYSTEM_LS_KEY) || '';
			setSelectedSystem(legacy);
		}
		setPage(1);
		setAllItems([]);
		setLastSeenFileName(null);
	}, [buildingId]);

	// Validate selectedSystem against loaded systems for the current building.
	useEffect(() => {
		if (systemsLoaded && systems && selectedSystem) {
			const exists = systems.find((s) => s.name === selectedSystem);
			if (!exists) {
				console.warn(
					`Selected system '${selectedSystem}' not available for current building. Clearing selection.`
				);
				setSelectedSystem('');
				localStorage.removeItem(keyenceSystemLsKey(buildingId));
			}
		}
	}, [systems, systemsLoaded, selectedSystem, buildingId]);

	const resetPagination = () => {
		setPage(1);
		setAllItems([]);
	};

	const { data, isLoading, isFetching, isError } = useGetInspectionPlansQuery(
		{
			search: searchTerm,
			queue_status: status,
			queue: 'keyence',
			device_name: selectedSystem || '',
			production_method: productionMethod || undefined,
			page,
			...(buildingId ? { building_id: buildingId } : {}),
		},
		{
			refetchOnMountOrArgChange: true,
			skip: status === 'Live',
		}
	);

	// Accumulate results across pages; data.previous === null means first page
	useEffect(() => {
		if (!data?.results) return;
		if (!data.previous) {
			setAllItems(data.results);
		} else {
			setAllItems((prev) => [...prev, ...data.results]);
		}
	}, [data]);

	// Auto-load next page if content doesn't overflow, or on scroll near bottom
	useEffect(() => {
		if (!data?.next || isFetching) return;

		const container = contentRef.current;
		if (!container) return;

		// Check after DOM updates whether content overflows
		const rafId = requestAnimationFrame(() => {
			if (container.scrollHeight <= container.clientHeight) {
				setPage((prev) => prev + 1);
			}
		});

		// Content overflows — use scroll listener
		const handleScroll = () => {
			if (!data?.next || isFetching) return;
			const { scrollTop, scrollHeight, clientHeight } = container;
			if (scrollHeight - scrollTop - clientHeight < 300) {
				setPage((prev) => prev + 1);
			}
		};
		container.addEventListener('scroll', handleScroll);
		return () => {
			cancelAnimationFrame(rafId);
			container.removeEventListener('scroll', handleScroll);
		};
	}, [data?.next, isFetching, allItems.length]);

	const {
		data: liveData,
		isFetching: liveLoading,
		isError: liveError,
		refetch: refetchLive,
	} = useGetRecentFileQuery(
		selectedSystem
			? {
					device_name: selectedSystem,
					...(buildingId ? { building_id: buildingId } : {}),
			  }
			: undefined,
		{
			skip: status !== 'Live' || !selectedSystem,
		}
	);

	const nextSample = (liveData as any)?.next_sample;
	const hasRecentReport = !!nextSample?.recent_report?.length;
	const hasReport = !!nextSample?.report;
	const shouldShowCard = hasRecentReport && !hasReport && !skipSampleRender;

	usePolling(
		status === 'Live' && pollingEnabled && !!selectedSystem,
		refetchLive,
		5000
	);

	useEffect(() => {
		const fileName = nextSample?.recent_report?.[0]?.file_name;

		if (fileName && hasReport && fileName !== lastSeenFileName) {
			setPollingEnabled(false);
			setShowSampleViewModal(true);
			setLastSeenFileName(fileName);
			return;
		}

		if (hasRecentReport && !hasReport && !skipSampleRender) {
			setPollingEnabled(false);
		}
	}, [
		nextSample,
		hasReport,
		hasRecentReport,
		lastSeenFileName,
		skipSampleRender,
	]);

	useEffect(() => {
		if (hasRecentReport && !hasReport) {
			setSkipSampleRender(false);
		}
	}, [hasRecentReport, hasReport]);

	useEffect(() => {
		if (status === 'Live') {
			setSkipSampleRender(false);
			setShowSampleViewModal(false);
			setLastSeenFileName(null);
			setPollingEnabled(true);
			if (selectedSystem) {
				refetchLive();
			}
		}
	}, [status, selectedSystem]);

	const handleSearch = (value: string) => {
		setSearchTerm(value);
		resetPagination();
		setSearchParams(value ? { search: value, status } : { status });
	};

	const handleRefresh = () => {
		setSearchTerm('');
		resetPagination();
		setSearchParams({ status });
	};

	const handleSystemChange = (value: string) => {
		setSelectedSystem(value);
		resetPagination();
		const key = keyenceSystemLsKey(buildingId);
		if (value) {
			localStorage.setItem(key, value);
			localStorage.setItem(LEGACY_KEYENCE_SYSTEM_LS_KEY, value);
		} else {
			localStorage.removeItem(key);
		}
	};

	const handleReload = () => {
		resetPagination();
	};

	const systemOptions = (systems || []).map((system) => ({
		value: system.name,
		label: system.name,
	}));

	return (
		<div className="flex flex-col w-full p-3">
			<div className="flex flex-col md:flex-row justify-between py-4 gap-4">
				<div className="flex items-center gap-3">
					<h1 className="text-2xl font-bold m-0" style={TABLET_TITLE_STYLE}>
						Keyence
					</h1>
					<BuildingIndicator />
				</div>

				<div className="flex items-center gap-4">
					<Select
						value={selectedSystem || undefined}
						placeholder="Select System"
						onChange={handleSystemChange}
						options={systemOptions}
						size="large"
						style={{ minWidth: 180 }}
						allowClear
						onClear={() => handleSystemChange('')}
					/>

					<Input.Search
						placeholder="Search Job/Product"
						allowClear
						onSearch={handleSearch}
						style={{ width: 250, ...TABLET_SEARCH_STYLE }}
						size="large"
					/>

					<Radio.Group
						value={status}
						onChange={(e) => {
							const newStatus = e.target.value;
							setStatus(newStatus);
							setLastSeenFileName(null);
							resetPagination();
							setSearchParams((prev) => {
								const newParams = new URLSearchParams(prev.toString());
								newParams.set('status', newStatus);
								return newParams;
							});
						}}
						size="large"
						optionType="button"
						buttonStyle="solid"
					>
						<Radio.Button value="Live" style={TABLET_RADIO_BUTTON_STYLE}>
							Live
						</Radio.Button>
						<Radio.Button value="In Progress" style={TABLET_RADIO_BUTTON_STYLE}>
							In Progress
						</Radio.Button>
						<Radio.Button value="Completed" style={TABLET_RADIO_BUTTON_STYLE}>
							Completed
						</Radio.Button>
					</Radio.Group>

					<Button
						icon={<ArrowLeftOutlined />}
						type="primary"
						onClick={() => navigate('/tablet/operations')}
						size="large"
						style={TABLET_BACK_BUTTON_STYLE}
					>
						Back
					</Button>
				</div>
			</div>

			<Divider style={{ marginBottom: 5 }} />

			<div
				ref={contentRef}
				style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}
			>
				{status === 'Live' ? (
					<div style={{ paddingTop: 16 }}>
						{!selectedSystem ? (
							<Flex justify="center" align="center" style={{ minHeight: 200 }}>
								<Empty description="Please select a Keyence System" />
							</Flex>
						) : liveLoading ? (
							<Flex justify="center" align="center" style={{ minHeight: 200 }}>
								<Spin size="large" />
							</Flex>
						) : liveError ? (
							<Flex justify="center" align="center" style={{ minHeight: 200 }}>
								<Empty description="Waiting for results..." />
							</Flex>
						) : shouldShowCard && liveData ? (
							<Card
								style={{
									border: '2px solid #1890ff',
									background: '#e6f7ff',
									boxShadow: '0 0 10px rgba(24, 144, 255, 0.4)',
									borderRadius: 8,
									width: '100%',
								}}
							>
								<KeyenceCard
									plan={liveData}
									lockSample={false}
									passedSample={nextSample}
									isLiveQueue
									deviceName={selectedSystem}
									onUpdate={() => {
										setPollingEnabled(true);
										refetchLive();
									}}
								/>
								<Button
									style={{ marginTop: 16 }}
									onClick={() => {
										setSkipSampleRender(true);
										setPollingEnabled(true);
									}}
								>
									Refresh
								</Button>
							</Card>
						) : (
							<Flex justify="center" align="center" style={{ minHeight: 200 }}>
								<Empty description="No live results" />
							</Flex>
						)}
					</div>
				) : (
					<>
						<div style={TABLET_PRODUCTION_METHOD_ROW_STYLE}>
							<Radio.Group
								value={productionMethod || 'All'}
								optionType="button"
								buttonStyle="solid"
								size="large"
								style={TABLET_PRODUCTION_METHOD_GROUP_STYLE}
								onChange={(e) => {
									const value = e.target.value === 'All' ? '' : e.target.value;
									setProductionMethod(value);
									resetPagination();
									setSearchParams((prev) => {
										const newParams = new URLSearchParams(prev.toString());
										if (!value) {
											newParams.delete('method');
										} else {
											newParams.set('method', value);
										}
										return newParams;
									});
								}}
							>
								<Radio.Button value="All" style={TABLET_RADIO_BUTTON_STYLE}>
									All
								</Radio.Button>
								<Radio.Button
									value="Fastlane"
									style={TABLET_RADIO_BUTTON_STYLE}
								>
									Fastlane
								</Radio.Button>
								<Radio.Button value="Swiss" style={TABLET_RADIO_BUTTON_STYLE}>
									Swiss
								</Radio.Button>
							</Radio.Group>
						</div>
						{isLoading ? (
							<div style={{ textAlign: 'center', paddingTop: 64 }}>
								<Spin size="large" />
							</div>
						) : isError && allItems.length === 0 ? (
							<Empty description="Failed to load data" />
						) : allItems.length === 0 ? (
							<Empty
								description="No current parts in progress"
								style={{ marginTop: 64 }}
							>
								<Button type="primary" onClick={handleRefresh}>
									Refresh
								</Button>
							</Empty>
						) : (
							<List
								dataSource={allItems}
								renderItem={(plan: InspectionPlan) => (
									<List.Item key={plan.id}>
										<KeyenceCard
											plan={plan}
											onUpdate={handleReload}
											deviceName={selectedSystem}
											showResults
										/>
									</List.Item>
								)}
								loadMore={
									<div style={{ textAlign: 'center' }}>
										<div ref={sentinelRef} style={{ height: 1 }} />
										{isFetching && <Spin style={{ margin: '16px 0' }} />}
									</div>
								}
							/>
						)}
					</>
				)}
			</div>

			<Modal
				open={showSampleViewModal}
				title="All view samples linked"
				onOk={() => {
					setShowSampleViewModal(false);
					setPollingEnabled(true);
					refetchLive();
				}}
				onCancel={() => {
					setShowSampleViewModal(false);
					setPollingEnabled(true);
					refetchLive();
				}}
				okText="OK"
			>
				<p>
					All samples for <strong>{nextSample?.view}</strong> view of part{' '}
					<strong>{liveData?.name}</strong> are already linked!
				</p>
			</Modal>
		</div>
	);
};

export default KeyenceLineItemList;
